import { ref, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import { capitalize } from 'lodash'
import { rails_url } from '@/modules/shared/utils/rails'
import { useActionsMenu } from '@/modules/shared/utils/actions-menu'
import { useTransactionStore } from '@/modules/investing/stores/transaction-store'
import { useBatchTransactionStore } from '@/modules/investing/stores/batch-transaction-store'
import { useCapitalCallStore } from '@/modules/investing/stores/capital-call-store'
import { useCallStore } from '@/modules/investing/stores/call-store'
import { useCompanyStore } from '@/modules/investing/stores/company-store'

type ActionCtx = { item: any; group?: any }

export type Action =
  | {
      kind?: 'item'
      text: string
      tag?: 'RouterLink' | 'a'
      visible?: (ctx: ActionCtx) => boolean
      to?: (ctx: ActionCtx) => any
      href?: (ctx: ActionCtx) => string
      onClick?: (ctx: ActionCtx) => void
      loading?: (ctx: ActionCtx) => boolean
    }
  | {
      kind: 'call_modal'
      visible?: (ctx: ActionCtx) => boolean
      props: (ctx: ActionCtx) => Record<string, any>
    }
  | {
      kind: 'distribution_modal'
      visible?: (ctx: ActionCtx) => boolean
      props: (ctx: ActionCtx) => Record<string, any>
    }
  | {
      kind: 'pd_modal'
      visible?: (ctx: ActionCtx) => boolean
      props: (ctx: ActionCtx) => Record<string, any>
    }

type Deps = {
  isAdmin: ComputedRef<boolean>
  isInvestor: ComputedRef<boolean>
  fetch: () => Promise<void>
}

export function useTransactionActions(deps: Deps) {
  const { isAdmin, isInvestor, fetch } = deps

  const route = useRoute()
  const { entity_id, entity_type, slug } = route.params as {
    entity_id: string
    entity_type: string
    slug: string
  }

  // Stores backing the imperative action handlers below.
  const transactionStore = useTransactionStore()
  const batchTransactionStore = useBatchTransactionStore()
  const capitalCallStore = useCapitalCallStore()
  const callStore = useCallStore()
  const companyStore = useCompanyStore()

  // Loading-state container shared by all actions on this page.
  const actionsMenu = useActionsMenu([
    'send_reminder',
    'delete',
    'mark_invoice_as_unpaid',
    'mark_distribution_as_unpaid',
    'revert_loss',
  ])

  // Tracks whether the table has rendered before — used to suppress auto-open
  // of the call details modal on subsequent v-data-table rerenders.
  const hasAlreadyLoaded = ref(false)

  const isCallOpenedByDefault = (callId: any) => {
    if (hasAlreadyLoaded.value) return false
    return route.query.call_id?.toString() === callId.toString()
  }

  const onCallDetailsOpened = () => {
    hasAlreadyLoaded.value = true
  }

  /////////////////////////////////////////////////////////////////////////////
  // Handlers
  /////////////////////////////////////////////////////////////////////////////

  const deleteTransaction = async (transactionId: string) => {
    if (!window.confirm('Are you sure?')) return
    actionsMenu.setLoading('delete', true)
    await transactionStore.removeTransaction(transactionId, { slug })
    await fetch()
    actionsMenu.setLoading('delete', false)
  }

  const deleteBatchTransaction = async (batchId: string) => {
    if (!window.confirm('Are you sure?')) return
    actionsMenu.setLoading('delete', true)
    await batchTransactionStore.removeBatchTransaction(batchId, { slug })
    await fetch()
    actionsMenu.setLoading('delete', false)
  }

  const undoRealizeLoss = async (company_id: any, investment_id: any) => {
    if (!window.confirm('Are you sure?')) return
    actionsMenu.setLoading('revert_loss', true)
    await companyStore.undoRealizeLoss(company_id, investment_id)
    await fetch()
    actionsMenu.setLoading('revert_loss', false)
  }

  const sendBatchInvoiceReminder = async (batchId: string) => {
    actionsMenu.setLoading('send_reminder', true)
    await capitalCallStore.sendInvoiceReminders(batchId, { slug })
    actionsMenu.setLoading('send_reminder', false)
  }

  const sendInvoiceReminder = async (transactionId: string) => {
    actionsMenu.setLoading('send_reminder', true)
    await callStore.sendInvoiceReminder(transactionId, { slug })
    actionsMenu.setLoading('send_reminder', false)
  }

  /////////////////////////////////////////////////////////////////////////////
  // Action config
  /////////////////////////////////////////////////////////////////////////////
  // Single source of truth: per type, per render mode, the actions that show.
  // What you see in this config is exactly what renders. No skip lists, no
  // delete-tail concat — if a type should show a Delete, its entry includes it.
  // Types without an explicit entry fall back to `default`.

  const actions: Record<string, { aggregate?: Action[]; flat?: Action[] }> = {
    capital_call: {
      aggregate: [
        {
          text: 'View',
          tag: 'RouterLink',
          visible: () => isAdmin.value || isInvestor.value,
          to: ({ group }) => ({
            name: 'investing.capital-call.edit',
            params: { capital_call_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Edit',
          tag: 'RouterLink',
          visible: ({ item }) => isAdmin.value,
          to: ({ group }) => ({
            name: 'investing.capital-call.edit',
            params: { capital_call_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Send invoice reminder',
          visible: ({ item, group }) => isAdmin.value && group?.items?.some((i: any) => i.invoice_id),
          onClick: ({ group }) => sendBatchInvoiceReminder(group?.items[0]?.transaction_batch_id),
          loading: () => actionsMenu.actions.value.send_reminder?.loading,
        },
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ group }) => deleteBatchTransaction(group?.items[0]?.transaction_batch_id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
      flat: [
        {
          kind: 'call_modal',
          visible: () => isAdmin.value || isInvestor.value,
          props: ({ item }) => ({
            callId: item.id,
            status: item.invoice_id ? capitalize(item.status) : null,
            isAdmin: isAdmin.value,
            isScheduled: item.display_type === 'scheduled_call',
            refetch: async () => await fetch(),
            isOpenByDefault: isCallOpenedByDefault(item.id),
            onOpenDetails: onCallDetailsOpened,
          }),
        },
        {
          text: 'Edit',
          tag: 'RouterLink',
          visible: ({ item }) => isAdmin.value,
          to: ({ item }) => ({
            name: 'investing.capital-call.edit',
            params: { capital_call_id: item.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Send invoice reminder',
          visible: ({ item }) => Boolean(item.invoice_id) && item.status !== 'paid' && isAdmin.value,
          onClick: ({ item }) => sendInvoiceReminder(item.id),
          loading: () => actionsMenu.actions.value.send_reminder?.loading,
        },
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ item }) => deleteTransaction(item.id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
    },

    scheduled_call: {
      aggregate: [
        {
          text: 'View',
          tag: 'RouterLink',
          visible: () => isAdmin.value || isInvestor.value,
          to: ({ group }) => ({
            name: 'investing.capital-call.edit',
            params: { capital_call_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Edit',
          tag: 'RouterLink',
          visible: ({ item }) => isAdmin.value,
          to: ({ group }) => ({
            name: 'investing.capital-call.edit',
            params: { capital_call_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Send invoice reminder',
          visible: ({ item, group }) => isAdmin.value && group?.items?.some((i: any) => i.invoice_id),
          onClick: ({ group }) => sendBatchInvoiceReminder(group?.items[0]?.transaction_batch_id),
          loading: () => actionsMenu.actions.value.send_reminder?.loading,
        },
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ group }) => deleteBatchTransaction(group?.items[0]?.transaction_batch_id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
      // No flat: scheduled-call rows have item.type === 'capital_call' in flat
      // mode (only display_type differs), so flat goes through `capital_call`.
    },

    cash_distribution: {
      aggregate: [
        {
          text: 'View',
          tag: 'RouterLink',
          visible: () => isAdmin.value || isInvestor.value,
          to: ({ group }) => ({
            name: 'investing.disbursement.edit',
            params: { disbursement_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Edit',
          tag: 'RouterLink',
          visible: ({ item }) => isAdmin.value,
          to: ({ group }) => ({
            name: 'investing.disbursement.edit',
            params: { disbursement_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ group }) => deleteBatchTransaction(group?.items[0]?.transaction_batch_id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
    },

    asset_funds: {
      aggregate: [
        {
          text: 'View',
          tag: 'RouterLink',
          visible: () => isAdmin.value || isInvestor.value,
          to: ({ group }) => ({
            name: 'investing.disbursement.edit',
            params: { disbursement_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Edit',
          tag: 'RouterLink',
          visible: ({ item }) => isAdmin.value,
          to: ({ group }) => ({
            name: 'investing.disbursement.edit',
            params: { disbursement_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ group }) => deleteBatchTransaction(group?.items[0]?.transaction_batch_id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
    },

    distribution: {
      flat: [
        {
          kind: 'distribution_modal',
          visible: ({ item }) => Boolean(item.id) || isAdmin.value || isInvestor.value,
          props: ({ item }) => ({
            distributionId: String(item.id),
            isAdmin: isAdmin.value,
            refetch: async () => await fetch(),
          }),
        },
        {
          text: 'View Company Distribution',
          tag: 'a',
          visible: ({ item }) => Boolean(item.company?.id) && (isAdmin.value || isInvestor.value),
          href: ({ item }) => `${rails_url()}/companies/${item.company.id}/disbursements`,
        },
        {
          text: 'Edit',
          tag: 'RouterLink',
          visible: ({ item }) => isAdmin.value,
          to: ({ item }) => ({
            name: 'investing.disbursement.edit',
            params: { disbursement_id: item.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ item }) => deleteTransaction(item.id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
    },

    portfolio_distribution: {
      aggregate: [
        {
          kind: 'pd_modal',
          visible: () => isAdmin.value || isInvestor.value,
          props: ({ item }) => ({ portfolioDistributionId: item.id }),
        },
        {
          text: 'Edit',
          tag: 'a',
          visible: ({ item }) => isAdmin.value,
          href: ({ item }) => `${rails_url()}/companies/${item.company.id}/disbursements`,
        },
      ],
      flat: [
        {
          kind: 'pd_modal',
          visible: () => isAdmin.value || isInvestor.value,
          props: ({ item }) => ({ portfolioDistributionId: item.id }),
        },
        {
          text: 'Edit',
          tag: 'a',
          visible: ({ item }) => isAdmin.value,
          href: ({ item }) => `${rails_url()}/companies/${item.company.id}/disbursements`,
        },
      ],
    },

    loss_investment: {
      aggregate: [
        {
          text: 'View',
          tag: 'a',
          visible: () => isAdmin.value || isInvestor.value,
          href: ({ item }) => `${rails_url()}/companies/${item.company.id}/valuations`,
        },
      ],
      flat: [
        {
          text: 'View',
          tag: 'a',
          visible: () => isAdmin.value || isInvestor.value,
          href: ({ item }) => `${rails_url()}/companies/${item.company.id}/valuations`,
        },
        {
          text: 'Revert realized loss',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ item }) => undoRealizeLoss(item.company.id, item.id),
          loading: () => actionsMenu.actions.value.revert_loss?.loading,
        },
      ],
    },

    interest_allocation_received: {
      aggregate: [
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ group }) => deleteBatchTransaction(group?.items[0]?.transaction_batch_id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
      flat: [
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ item }) => deleteTransaction(item.id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
    },

    interest_allocation_paid: {
      aggregate: [
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ group }) => deleteBatchTransaction(group?.items[0]?.transaction_batch_id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
      flat: [
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ item }) => deleteTransaction(item.id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
    },

    default: {
      aggregate: [
        {
          text: 'View',
          tag: 'RouterLink',
          visible: () => isAdmin.value || isInvestor.value,
          to: ({ group }) => ({
            name: 'investing.transactions.edit',
            params: { batch_transaction_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Edit',
          tag: 'RouterLink',
          visible: ({ item }) => isAdmin.value,
          to: ({ group }) => ({
            name: 'investing.transactions.edit',
            params: { batch_transaction_id: group?.items[0]?.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ group }) => deleteBatchTransaction(group?.items[0]?.transaction_batch_id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
      flat: [
        {
          text: 'View',
          tag: 'RouterLink',
          visible: () => isAdmin.value || isInvestor.value,
          to: ({ item }) => ({
            name: 'investing.transactions.edit',
            params: { batch_transaction_id: item.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Edit',
          tag: 'RouterLink',
          visible: ({ item }) => isAdmin.value,
          to: ({ item }) => ({
            name: 'investing.transactions.edit',
            params: { batch_transaction_id: item.transaction_batch_id, entity_id, entity_type },
          }),
        },
        {
          text: 'Delete',
          visible: ({ item }) => isAdmin.value,
          onClick: ({ item }) => deleteTransaction(item.id),
          loading: () => actionsMenu.actions.value.delete?.loading,
        },
      ],
    },
  }

  const resolveActions = (item: any, mode: 'aggregate' | 'flatten' | 'subrow'): Action[] => {
    const entry = actions[item.type] ?? actions.default
    return (mode === 'aggregate' ? entry.aggregate : entry.flat) ?? []
  }

  return { resolveActions }
}
